import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatFullDate } from "@/components/tickets/config";
import {
  buildAdminBookingTimeline,
  type AdminBookingTimelineStep,
} from "./buildAdminBookingTimeline";
import type { BookingLifecycleStatus } from "@/types";

interface BookingAdminTimelineProps {
  status: BookingLifecycleStatus;
  requiresHandshake: boolean;
  /** When canceled, whether the booking had reached ACCEPTED first. */
  reachedAccepted: boolean;
  createdAt: string;
  updatedAt: string;
}

function StepMarker({
  step,
  isLast,
}: {
  step: AdminBookingTimelineStep;
  isLast: boolean;
}) {
  const base =
    "relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full";

  if (step.tone === "upcoming") {
    return (
      <span
        className={cn(base, "border-2 border-surface-300 bg-surface-0")}
        aria-hidden
      />
    );
  }

  if (step.tone === "failure") {
    return (
      <span
        className={cn(
          base,
          "bg-error-fill text-white",
          step.isCurrent && "ring-4 ring-error-500/15",
        )}
        aria-hidden
      >
        {step.isCurrent ? (
          <X className="h-3.5 w-3.5" strokeWidth={3} />
        ) : (
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        )}
      </span>
    );
  }

  if (step.isCurrent && !isLast) {
    return (
      <span
        className={cn(
          base,
          "border-2 border-success-500 bg-surface-0 ring-4 ring-success-500/15",
        )}
        aria-hidden
      >
        <span className="h-2 w-2 rounded-full bg-success-500" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        base,
        "bg-success-fill text-white",
        step.isCurrent && "ring-4 ring-success-500/15",
      )}
      aria-hidden
    >
      <Check className="h-3.5 w-3.5" strokeWidth={3} />
    </span>
  );
}

function connectorClass(
  left: AdminBookingTimelineStep,
  right: AdminBookingTimelineStep,
  isFailurePath: boolean,
): string {
  const filled = left.tone !== "upcoming" && right.tone !== "upcoming";
  if (!filled) return "bg-surface-200";
  return isFailurePath ? "bg-error-fill" : "bg-success-fill";
}

export function BookingAdminTimeline({
  status,
  requiresHandshake,
  reachedAccepted,
  createdAt,
  updatedAt,
}: BookingAdminTimelineProps) {
  const { t } = useTranslation("bookings");
  const { steps, isFailurePath } = buildAdminBookingTimeline(
    status,
    requiresHandshake,
    reachedAccepted,
  );
  const lastIndex = steps.length - 1;

  return (
    <div className="overflow-x-auto px-0.5 py-1">
      <ol className="flex min-w-[28rem] items-start gap-0">
        {steps.map((step, index) => {
          const isFirst = index === 0;
          const isLast = index === lastIndex;
          const prev = index > 0 ? steps[index - 1] : null;
          const next = !isLast ? steps[index + 1] : null;
          const timestamp =
            step.status === "CREATED"
              ? formatFullDate(createdAt)
              : step.isCurrent
                ? formatFullDate(updatedAt)
                : null;

          return (
            <li
              key={`${step.status}-${index}`}
              className={cn(
                "flex min-w-0 flex-1 flex-col",
                isFirst && "items-start",
                !isFirst && !isLast && "items-center",
                isLast && "items-end",
              )}
            >
              <div className="flex w-full items-center">
                {prev ? (
                  <div
                    className={cn(
                      "mr-1.5 h-0.5 flex-1",
                      connectorClass(prev, step, isFailurePath),
                    )}
                    aria-hidden
                  />
                ) : null}
                <StepMarker step={step} isLast={isLast} />
                {next ? (
                  <div
                    className={cn(
                      "ml-1.5 h-0.5 flex-1",
                      connectorClass(step, next, isFailurePath),
                    )}
                    aria-hidden
                  />
                ) : null}
              </div>
              <div
                className={cn(
                  "mt-2.5 max-w-[10rem]",
                  isFirst && "text-left",
                  !isFirst && !isLast && "text-center",
                  isLast && "text-right",
                )}
              >
                <p
                  className={cn(
                    "text-[13px] font-medium leading-snug",
                    step.tone === "upcoming"
                      ? "text-surface-400"
                      : step.isCurrent
                        ? "font-semibold text-surface-900"
                        : "text-surface-700",
                  )}
                >
                  {t(`adminDetail.timeline.steps.${step.status}`)}
                </p>
                {timestamp ? (
                  <p className="mt-0.5 text-xs tabular-nums text-surface-500">
                    {timestamp}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
